import { Link } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';
import LayoutGap from '@/components/Common/LayoutGap';
import PageHeader from '@/components/Common/PageHeader';
import Button from '@/components/Common/Button';
import { ColumnDef, DataTable } from '@/components/ui/data-table';
import { useGetExperiments, ExperimentResponse } from '@/hooks/api/experiments';
import { DisplayTimestamp } from '@/components/Common/DisplayData';
import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/Input';
import { EntityCard } from '@/components/ui/EntityCard';

const columns: ColumnDef<ExperimentResponse>[] = [
  {
    header: 'Name',
    accessorKey: 'attributes.name',
    cell: ({ row }) => (
      <EntityCard
        className="gap-0"
        entity={row.original}
        linkTo={{ path: `/experiments/${row.original.id}` }}
      />
    ),
  },
  {
    header: 'Created At',
    accessorKey: 'createdAt',
    cell: ({ row }) => <DisplayTimestamp timestamp={row.original.createdAt} />,
  },
  {
    header: 'Updated At',
    accessorKey: 'updatedAt',
    cell: ({ row }) => <DisplayTimestamp timestamp={row.original.updatedAt} />,
  },
  {
    header: 'More Info',
    cell: ({ row }) => (
      <Link to={`/experiments/${row.original.id}`}>
        <Button variant="secondary" size="small">
          View/Edit
        </Button>
      </Link>
    ),
  },
];

const isMatchingSearchTerm = (
  experiment: ExperimentResponse,
  searchTerm: string,
) =>
  experiment.attributes.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  experiment.id.toLowerCase().includes(searchTerm.toLowerCase());

const Experiments = () => {
  const [experiments] = useGetExperiments();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredExperiments = useMemo(
    () =>
      experiments.data?.data.filter((experiment) =>
        isMatchingSearchTerm(experiment, searchTerm),
      ),
    [experiments.data?.data, searchTerm],
  );

  return (
    <LayoutGap direction="column">
      <PageHeader title="Experiments" className="p-4 pb-0">
        <Link to="/experiments/create">
          <Button icon={<PlusIcon size="1em" />}>Create Experiment</Button>
        </Link>
      </PageHeader>
      <LayoutGap direction="column" className="px-4">
        <Input
          placeholder="Filter experiments by name or ID"
          className="w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </LayoutGap>
      <div className="relative h-[calc(100vh-14.5rem)] overflow-y-auto">
        <DataTable
          columns={columns}
          {...experiments}
          data={filteredExperiments}
        />
      </div>
    </LayoutGap>
  );
};

export default Experiments;
